import React from "react";
import { useThemeMode } from "../../../context/contextTheme";
import { useMalware } from "../../../context/Dashboard/contextMalware";
import { useDesign } from "../../../context/contextDesign";
import hexToRGBA from "../../../utils/hexToRGBA";

const OnloadUrlUrl = () => {
  const { activeTabResult, handleClickResult } = useMalware();
  const { viewHeight } = useDesign();
  const { theme } = useThemeMode();

  const tabButtonStyle = (isActive) => ({
    height: 45,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.9rem",
    color: isActive ? theme.textColor : theme.textMuted,
    borderBottom: isActive ? `3px solid #344266` : "3px solid transparent",
    transition: "all 0.2s ease",
    background: "transparent",
    borderLeft: 0,
    borderRight: 0,
    borderTop: 0,
    borderRadius: 0,
  });

  return (
    <div className="w-100 px-4 py-2">
      <HeaderView />
      
      <div className="mt-4 px-2">
        <div className="d-flex align-items-center mb-3">
          <button 
            className="px-4 me-2" 
            style={tabButtonStyle(activeTabResult === 'Detection')}
            onClick={() => handleClickResult('Detection')}
          >
            DETECTION
          </button>
          <button 
            className="px-4" 
            style={tabButtonStyle(activeTabResult === 'Details')}
            onClick={() => handleClickResult('Details')}
          >
            DETAILS
          </button>
        </div>

        <div className="overflow-auto custom-scrollbar" style={{ height: viewHeight }}>
          {activeTabResult === 'Detection' && <DetectionView />}
          {activeTabResult === 'Details' && <DetailsView />}
        </div>
      </div>
    </div>
  );
};

function HeaderView() {
  const { response } = useMalware();
  const attr = response?.attributes;
  const url = attr?.url || "N/A";
  const status_code = attr?.last_http_response_code || 0;
  const content_type = attr?.last_http_response_headers?.["Content-Type"] || "N/A";
  const date_of_last_scan = convertToRelativeTime(attr?.last_analysis_date);

  return (
    <div className="card border-0 rounded-4 p-4 shadow-sm text-white" style={{ background: '#344266' }}>
      <div className="row align-items-center">
        <div className="col-lg-6 mb-3 mb-lg-0">
          <div className="small opacity-75 mb-1">Scanned URL</div>
          <h5 className="text-truncate fw-bold mb-0">
            <i className="bi bi-globe2 me-2" />{url}
          </h5>
        </div>
        <div className="col-lg-6">
          <div className="d-flex justify-content-lg-end gap-4 text-center">
            <div>
              <div className="small opacity-75 mb-1">Status</div>
              <div className="fw-bold d-flex align-items-center">
                <i className={`bi bi-${status_code === 200 ? 'check-circle' : 'exclamation-triangle'} me-2`} />
                {status_code}
              </div>
            </div>
            <div className="vr opacity-25"></div>
            <div>
              <div className="small opacity-75 mb-1">Type</div>
              <div className="fw-bold">{content_type.split(';')[0]}</div>
            </div>
            <div className="vr opacity-25"></div>
            <div>
              <div className="small opacity-75 mb-1">Last Scan</div>
              <div className="fw-bold">{date_of_last_scan}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetectionView() {
  const { response } = useMalware();
  const { theme } = useThemeMode();
  const results = response?.attributes?.last_analysis_results || {};

  return (
    <div className="card border-0 rounded-4 shadow-sm" style={{ backgroundColor: theme.labelBgMuted }}>
      <div className="table-responsive p-3">
        <table className="table table-borderless align-middle mb-0" style={{ color: theme.textColor }}>
          <thead className="text-muted small">
            <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
              <th className="pb-3">ENGINE NAME</th>
              <th className="pb-3 text-center">CATEGORY</th>
              <th className="pb-3 text-end">RESULT</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(results).map((key) => {
              const r = results[key];
              const isClean = r.result === 'clean';
              return (
                <tr key={key} style={{ borderBottom: `1px solid ${hexToRGBA(theme.border, 0.5)}` }}>
                  <td className="py-3 fw-bold">{r.engine_name}</td>
                  <td className="py-3 text-center text-muted small">{r.category}</td>
                  <td className="py-3 text-end">
                    <span className={`badge rounded-pill px-3 py-2`} 
                      style={{ 
                        backgroundColor: isClean ? hexToRGBA('#00a73d', 0.1) : hexToRGBA('#dc3545', 0.1),
                        color: isClean ? '#00a73d' : '#dc3545',
                        fontSize: '0.75rem'
                      }}>
                      <i className={`bi bi-${isClean ? 'shield-check' : 'bug'} me-1`} />
                      {r.result.toUpperCase()}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailsView() {
  const { response } = useMalware();
  const { theme } = useThemeMode();
  const attr = response.attributes;

  const DetailCard = ({ title, children, icon }) => (
    <div className="col-md-6 col-xl-4 mb-4">
      <div className="card h-100 border-0 rounded-4 shadow-sm" style={{ backgroundColor: theme.labelBgMuted }}>
        <div className="card-body p-4">
          <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: theme.textColor }}>
            <i className={`bi bi-${icon} me-2`} style={{ color: '#344266' }} />
            {title}
          </h6>
          <div className="small" style={{ color: theme.textColor }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="row">
      <DetailCard title="HTTP Metadata" icon="file-earmark-code">
        <div className="d-flex justify-content-between mb-2"><span>Length:</span> <b>{attr.last_http_response_content_length} KB</b></div>
        <div className="text-break"><span>SHA-256:</span><br/><b className="text-muted">{attr.last_http_response_content_sha256}</b></div>
      </DetailCard>

      <DetailCard title="Reputation & Votes" icon="hand-thumbs-up">
        <div className="d-flex justify-content-between mb-2 text-success"><span>Harmless:</span> <b>{attr.total_votes?.harmless}</b></div>
        <div className="d-flex justify-content-between text-danger"><span>Malicious:</span> <b>{attr.total_votes?.malicious}</b></div>
      </DetailCard>

      <DetailCard title="Categories" icon="tags">
        {Object.entries(attr.categories || {}).map(([key, val]) => (
          <div key={key} className="mb-1 d-flex justify-content-between">
            <span className="text-muted">{key}:</span> <b>{val}</b>
          </div>
        ))}
      </DetailCard>

      <DetailCard title="Submission Timeline" icon="clock-history">
        <div className="mb-2">First: <br/><b>{new Date(attr.first_submission_date * 1000).toLocaleString()}</b></div>
        <div>Last: <br/><b>{new Date(attr.last_submission_date * 1000).toLocaleString()}</b></div>
      </DetailCard>
    </div>
  );
}

const convertToRelativeTime = (timestamp) => {
  if (!timestamp) return "N/A";
  const timeDifference = Math.floor(Date.now() / 1000) - timestamp;
  if (timeDifference < 60) return `${timeDifference}s ago`;
  if (timeDifference < 3600) return `${Math.floor(timeDifference / 60)}m ago`;
  if (timeDifference < 86400) return `${Math.floor(timeDifference / 3600)}h ago`;
  return `${Math.floor(timeDifference / 86400)}d ago`;
};

export default OnloadUrlUrl;